/**
 * Simple Accessible Lightbox
 * Shows one image at a time with keyboard navigation and accessibility features
 */
class Lightbox {
  constructor() {
    this.lightbox = null;
    this.isOpen = false;
    this.focusedElementBeforeOpen = null;

    this.init();
  }

  init() {
    this.createLightboxHTML();
    this.bindEvents();
  }

  createLightboxHTML() {
    // Create lightbox container
    this.lightbox = document.createElement('div');
    this.lightbox.className = 'lightbox';
    this.lightbox.setAttribute('role', 'dialog');
    this.lightbox.setAttribute('aria-modal', 'true');
    this.lightbox.setAttribute('aria-label', 'Image viewer');
    this.lightbox.style.display = 'none';

    this.lightbox.innerHTML = `
      <div class="lightbox__backdrop" aria-hidden="true"></div>
      <div class="lightbox__content">
        <div class="lightbox__container">
          <img class="lightbox__image u-radius20" src="" alt="" />
          <button class="lightbox__close" type="button" aria-label="Close image viewer">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(this.lightbox);
  }

  bindEvents() {
    // Find all images that should open in lightbox
    // You can customize this selector based on your needs
    const images = document.querySelectorAll('img[data-lightbox], .lightbox-trigger img, .gallery img');

    images.forEach(img => {
      img.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(img);
      });

      // Make images keyboard accessible
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', `Open image in lightbox: ${img.alt || 'Image'}`);

      img.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open(img);
        }
      });
    });

    // Close button
    const closeBtn = this.lightbox.querySelector('.lightbox__close');
    closeBtn.addEventListener('click', () => this.close());

    // Close on backdrop click
    const backdrop = this.lightbox.querySelector('.lightbox__backdrop');
    backdrop.addEventListener('click', () => this.close());

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'Tab':
          this.trapFocus(e);
          break;
      }
    });
  }

  open(img) {
    // Store the focused element before opening
    this.focusedElementBeforeOpen = document.activeElement;

    // Get image source - check for high-res version first
    const imgSrc = img.getAttribute('data-lightbox-src') ||
                   img.getAttribute('data-full-url') ||
                   img.src;

    // Set image in lightbox
    const lightboxImg = this.lightbox.querySelector('.lightbox__image');
    lightboxImg.src = imgSrc;
    lightboxImg.alt = img.alt || 'Image';

    // Show lightbox
    this.lightbox.style.display = 'flex';
    this.isOpen = true;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus management
    setTimeout(() => {
      const closeBtn = this.lightbox.querySelector('.lightbox__close');
      closeBtn.focus();
    }, 100);

    // Add class for animations
    requestAnimationFrame(() => {
      this.lightbox.classList.add('lightbox--active');
    });
  }

  close() {
    if (!this.isOpen) return;

    this.lightbox.classList.remove('lightbox--active');
    this.isOpen = false;

    // Restore body scroll
    document.body.style.overflow = '';

    // Hide after animation
    setTimeout(() => {
      this.lightbox.style.display = 'none';
    }, 300);

    // Restore focus
    if (this.focusedElementBeforeOpen) {
      this.focusedElementBeforeOpen.focus();
    }
  }

  trapFocus(e) {
    const focusableElements = this.lightbox.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  }
}

// Initialize lightbox when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new Lightbox();
});

// Export for potential use in other modules
export default Lightbox;